import React, { useContext, useState } from 'react';
import {
  IEtf,
  IEtfValue,
  IEtfValueInput,
  useAddEtfValueMutation,
  useUpdateEtfValueMutation
} from '../../generated/graphql';
import { Box, Button, Link } from '@mui/material';
import { Colors, buttons } from '../../utils/theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Edit } from '@mui/icons-material';
import { parseDate, getNullableNumberInput } from '../../utils/clientUtils';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import DialogBox from '../Common/Layout/DialogBox';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import InputComponent from '../Common/Fields/InputComponent';

type Props = {
  handleClose: (saved: boolean, isNew?: boolean, etfValue?: IEtfValue) => void;
  etf: IEtf;
  etfValueInput: IEtfValue | null;
};

type IErrors = {
  createDate: string | null;
  returnOneYear: string | null;
  aum: string | null;
  indexReturnOneYear: string | null;
};

const defaultErrorsObj: IErrors = {
  createDate: null,
  returnOneYear: null,
  aum: null,
  indexReturnOneYear: null
};

const errorTexts = {
  createDate: 'Create date is required!',
  returnOneYear: 'Total return is required!',
  indexReturnOneYear: 'Index Total return is required!',
  aum: 'AUM is required!'
};

const EtfValueForm: React.FunctionComponent<Props> = ({
  handleClose,
  etf,
  etfValueInput
}: Props) => {
  const { dispatch } = useContext(AppContext);
  const [editable, setEditable] = useState(etfValueInput === null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createdDate, setCreatedDate] = useState<Date | null>(
    etfValueInput ? new Date(etfValueInput.date) : new Date()
  );
  const [returnOneYear, setReturnOneYear] = useState(etfValueInput?.total_return_one_year || '');
  const [aum, setAum] = useState(etfValueInput?.aum || '');
  const [indexReturnOneYear, setIndexReturnOneYear] = useState(
    etfValueInput?.index_total_return_one_year || ''
  );
  const [returnThreeYear, setReturnThreeYear] = useState(
    etfValueInput?.total_return_three_year || ''
  );
  const [indexReturnThreeYear, setIndexReturnThreeYear] = useState(
    etfValueInput?.index_total_return_three_year || ''
  );
  const [addEtfValue, addEtfValueResult] = useAddEtfValueMutation();
  const [updateEtfValue, updateEtfValueResult] = useUpdateEtfValueMutation();

  const handleCancel = () => {
    if (editable && etfValueInput !== null) {
      setCreatedDate(new Date(etfValueInput.date));
      setReturnOneYear(etfValueInput.total_return_one_year);
      setAum(etfValueInput.aum);
      setIndexReturnOneYear(etfValueInput.index_total_return_one_year);
      setReturnThreeYear(etfValueInput.total_return_three_year || '');
      setIndexReturnThreeYear(etfValueInput.total_return_three_year || '');
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, createDate: null });
      }
    } else {
      setFormErrors({ ...formErrors, createDate: errorTexts.createDate });
    }
    setCreatedDate(input);
  };

  const handleReturnOneYearChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setFormErrors({ ...formErrors, returnOneYear: value ? null : errorTexts.returnOneYear });
    setReturnOneYear(value || '');
  };

  const handleReturnThreeYearChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setReturnThreeYear(value || '');
  };

  const handleIndexReturnOneYearChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setFormErrors({
      ...formErrors,
      indexReturnOneYear: value ? null : errorTexts.indexReturnOneYear
    });
    setIndexReturnOneYear(value || '');
  };

  const handleIndexReturnThreeYearChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setIndexReturnThreeYear(value || '');
  };

  const handleAumChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setFormErrors({ ...formErrors, aum: value ? null : errorTexts.aum });
    setAum(value || '');
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      returnOneYear: null,
      aum: null,
      createDate: null,
      indexReturnOneYear: null
    };
    if (!createdDate) {
      hasErrors = true;
      errors.createDate = errorTexts.createDate;
    }
    if (returnOneYear === '' || Number.isNaN(returnOneYear)) {
      hasErrors = true;
      errors.returnOneYear = errorTexts.returnOneYear;
    }
    if (aum === '' || Number.isNaN(aum)) {
      hasErrors = true;
      errors.aum = errorTexts.aum;
    }
    if (indexReturnOneYear === '' || Number.isNaN(indexReturnOneYear)) {
      hasErrors = true;
      errors.indexReturnOneYear = errorTexts.indexReturnOneYear;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(etfValueInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    const input = {
      date: (createdDate as Date).toString(),
      aum,
      index_total_return_one_year: indexReturnOneYear,
      total_return_one_year: returnOneYear,
      index_total_return_three_year: getNullableNumberInput(indexReturnThreeYear.toString()),
      total_return_three_year: getNullableNumberInput(returnThreeYear.toString())
    } as IEtfValueInput;
    if (type === 'create') {
      addEtfValue({
        variables: {
          etf_id: etf.id,
          input
        },
        update(_, { data }) {
          const newValue = data?.result as IEtfValue;
          handleClose(true, true, newValue);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Etf value added for ${newValue.date}`
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      updateEtfValue({
        variables: {
          id: etfValueInput?.id as string,
          input
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IEtfValue);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Etf Value updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="etf-value-form"
      width={690}
      title={etfValueInput ? 'Etf Value Details' : 'Create ETF Value'}
    >
      {etfValueInput && (
        <Box sx={{ textAlign: 'right', marginRight: '15px', marginTop: '10px' }}>
          <Button
            id="ftse-form-edit-button"
            data-testid="ftse-form-edit-button"
            component={Link}
            onClick={() => setEditable(true)}
            disabled={editable}
            sx={{ ...buttons.edit, fontWeight: 'bold' }}
          >
            <Edit
              sx={{
                fontSize: 'medium',
                color: editable ? Colors.gray : Colors.skyBlue,
                marginRight: '3'
              }}
            />
            EDIT
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gap: '35px',
          m: '42px',
          gridTemplateRows: 'max-content'
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="etf-date-picker"
            label="ETF Launch Date"
            value={parseDate(etf.launch_date)}
            disabled
            format={'dd/MM/yyyy'}
            clearable={true}
            onChange={() => true}
            sx={{ gridColumn: 'span 6' }}
          />
        </LocalizationProvider>
        <InputComponent
          id="etf-provider"
          label="ETF Provider"
          value={etf.provider as string}
          disabled
          required
          additionalStyle={{ gridColumn: 'span 12' }}
        />
        <InputComponent
          id="etf-name"
          label="ETF Name"
          value={etf.name as string}
          disabled
          required
          additionalStyle={{ gridColumn: 'span 12' }}
        />
        <InputComponent
          id="etf-benchmark"
          label="Benchmark Name"
          required
          value={etf.identifier as string}
          disabled
          additionalStyle={{ gridColumn: 'span 12' }}
        />
        <Box
          sx={{
            color: Colors.main,
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            gridColumn: 'span 12',
            mb: '10px'
          }}
        >
          ETF VALUE
        </Box>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="etf-value-date-picker"
            label="Created Date"
            value={createdDate}
            disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
            format={'dd/MM/yyyy'}
            clearable={true}
            onChange={(d) => handleDateChange(d)}
            errorMsg={formErrors.createDate}
            sx={{ gridColumn: 'span 6', mb: '10px' }}
          />
        </LocalizationProvider>
        <InputComponent
          id="value-aum"
          label="Asset Under Management USD"
          required
          value={aum}
          type="number"
          onChange={(e) => handleAumChange(e)}
          disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
          errorMsg={formErrors.aum}
          additionalStyle={{ gridColumn: 'span 6' }}
        />
        <InputComponent
          id="value-one-year-return"
          label="ETF 1yr total return %"
          required
          type="number"
          value={returnOneYear}
          onChange={(e) => handleReturnOneYearChange(e)}
          disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
          errorMsg={formErrors.returnOneYear}
          additionalStyle={{ gridColumn: 'span 6' }}
        />
        <InputComponent
          id="value-index-one-year-return"
          label="Index 1yr total return %"
          required
          type="number"
          value={indexReturnOneYear}
          onChange={(e) => handleIndexReturnOneYearChange(e)}
          disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
          errorMsg={formErrors.indexReturnOneYear}
          additionalStyle={{ gridColumn: 'span 6' }}
        />
        <InputComponent
          id="value-three-year-return"
          label="ETF 3yr total return %"
          value={returnThreeYear}
          type="number"
          required
          onChange={(e) => handleReturnThreeYearChange(e)}
          disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
          additionalStyle={{ gridColumn: 'span 6' }}
        />
        <InputComponent
          id="value-index-three-year-return"
          label="Index 3yr total return %"
          value={indexReturnThreeYear}
          type="number"
          onChange={(e) => handleIndexReturnThreeYearChange(e)}
          disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
          additionalStyle={{ gridColumn: 'span 6' }}
        />
      </Box>
      <Box className="centralize" sx={{ marginTop: '50px', mb: '50px' }}>
        <CancelButton
          id="cancel-etf-value-button"
          data-testid="cancel-etf-value-button"
          disabled={addEtfValueResult.loading || updateEtfValueResult.loading}
          onClick={handleCancel}
          text={etfValueInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-etf-value-button"
          data-testid="save-etf-value-button"
          disabled={!editable || addEtfValueResult.loading || updateEtfValueResult.loading}
          onClick={handleSubmit}
          text={etfValueInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default EtfValueForm;
